import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { wp } from "../api/WpApi";
import data from "./_setup.json";
import PageLoading from "./PageLoading";
import { Helmet } from "react-helmet";
import parse from "html-react-parser";
import DEFAULT_THUMBNAIL from "../../resources/not-found.png";
import { ItemListSchema } from "../seo/ItemListSchema";
import Breadcrumbs from "../helpers/Breadcrumbs";
import { parseImgurThumbnail } from "../helpers/ImgurImageParser";

const CollectionView = () => {
  const { categoryName } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState(null);
  const [posts, setPosts] = useState([]);

  const fetchForCategory = useCallback((slug) => {
    setIsLoading(true);
    wp.categories()
      .slug(slug)
      .then((cats) => {
        if (!Array.isArray(cats) || cats.length === 0) {
          throw new Error("Category not found");
        }
        const cat = cats[0];
        setCategory(cat);
        return wp.posts().categories(cat.id).perPage(100).page(1);
      })
      .then((fetchedPosts) => {
        // if api returns paging wrapper
        if (fetchedPosts && fetchedPosts.length === 0 && fetchedPosts._paging) {
          setPosts([]);
        } else {
          // fetchedPosts may be array
          setPosts(Array.isArray(fetchedPosts) ? fetchedPosts : []);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (categoryName) fetchForCategory(categoryName);
  }, [categoryName, fetchForCategory]);

  if (error) {
    return (
      <div className="content">
        <Helmet>
          <meta name="robots" content="noindex" />
        </Helmet>
        Error: {error.message}
      </div>
    );
  }

  if (isLoading) return (<div className="container"><PageLoading/></div>);

  if (!category) return <div className="content">Category not found.</div>;

  // Only show collection pages for categories with more than 3 posts
  if (category.count < 3) {
    return (
      <div className="content container">
        <Helmet>
          <title>{`Collection: ${category.name} | ${"Andrew's Cookin'"}`}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h1>{`Collection: ${category.name}`}</h1>
        <p>This category has fewer than 3 recipes and is not eligible for a collection page.</p>
      </div>
    );
  }

  // Title like "12 Best Breakfast Recipes" — use number from posts length or cap at 12
  const total = posts.length;
  // const cap = Math.min(total, 12);
  const pageTitle = `Top ${total} Best ${category.name} Recipes`;
  const description = `A curated list of the Best ${category.name} recipes. Discover real, tested recipes that you can give a try!`;

  // Build ItemList schema for SEO (ordered list of items)
  const itemList = posts.map((p, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    url: `${process.env.REACT_APP_PUBLIC_URL}/${data.url.slug_posts}/${p.slug}/`,
  }));

  const breadcrumbsItems = [
    { name: "Home", url: "/" },
    { name: "Collections", url: `/collections/` },
    {
      name: category.name,
      url: `/collections/${category.slug}/`,
    },
  ];

  return (
    <div className="content container">
      <Helmet>
        <title>{pageTitle} | Andrew's Cookin'</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`${process.env.REACT_APP_PUBLIC_URL}/collections/${category.slug}/`} />
        <script type="application/ld+json">{JSON.stringify(ItemListSchema(itemList))}</script>
      </Helmet>

      <h1 className="fw-bold">{pageTitle}</h1>
      <p className="lead">{description}</p>
      <Breadcrumbs items={breadcrumbsItems} />
      <hr />

      <div className="post-text-wrapper text-left">
      People love <span className="lowercase">{category.name}</span> recipes, so I’ve curated this collection of the {total} best <span className="lowercase">{category.name}</span> recipes on Andrew’s Cookin’. Each recipe in this collection has been tested and approved by our team of one (a.k.a. Andrew), so you can cook with confidence.

      <ol type="1" className="post-list">
        {posts.map((post) => {
          const excerpt = post.excerpt && post.excerpt.rendered ? post.excerpt.rendered : "";
          // pick thumbnail from yoast_head_json if available
          let thumbnail = DEFAULT_THUMBNAIL;
          if (post.yoast_head_json && post.yoast_head_json.og_image && post.yoast_head_json.og_image.length > 0) {
            thumbnail = post.yoast_head_json.og_image[0].url;
          }
          return (
            <li key={post.id} >
              <h3 className="post-counter mt-2">
                <a className="row-span-1" href={`/${data.url.slug_posts}/${post.slug}/`}>
                  {post.title && post.title.rendered ? parse(post.title.rendered) : post.slug}
                </a>
              </h3>
              <div className="flow flow-rows-3 row-span-1 mb-4">
                
              <div className="row-span-1 d-flex justify-content-center">
                <img 
                  src={parseImgurThumbnail(thumbnail)}
                  alt={post.title && post.title.rendered ? (post.title.rendered.replace(/<[^>]+>/g, "")) : post.slug}
                  style={{ maxWidth: 720, width: "100%", height: "auto"}}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = DEFAULT_THUMBNAIL;
                  }}
                />
              </div>
                <span className="row-span-1" dangerouslySetInnerHTML={{ __html: excerpt }} />
                <a className="row-span-1" href={`/${data.url.slug_posts}/${post.slug}/`}>
                  Recipe: <span className="font-bold">{post.title && post.title.rendered ? parse(post.title.rendered) : post.slug}</span>
                </a>
              </div>
             
            </li>
          );
        })}
      </ol>

      Hope you found the recipe you were looking for in this collection! Maybe this will be your go to recipe one day. Check back often as we update these collections with new and delicious recipes.
        <hr />
        </div>
      <p>
        <a type="button" className="btn btn-primary" href={`/${data.url.slug_posts}/`}>Browse all recipes</a>
      </p>
    </div>
  );
};

export default CollectionView;
